/**
 * Screener filter engine — implements FILTER_FUNCTIONAL_SPEC.md.
 *
 * One registry drives control rendering, tab badges, criteria summaries,
 * defaults, saved-state validation, and the canonical `matchesStock`
 * predicate used by every view and the CSV export.
 *
 * Controls in the spec whose data the pipeline does not produce are omitted
 * (see UNAVAILABLE_SPEC_FILTERS): Top Rank, FCF Yield, Target Upside,
 * Altman Z, ROIC, Debt/Equity, Earnings date, and the IA9 configured
 * watchlist. Z 50d is replaced by the pipeline's 60-day Z-score.
 */

export const SCHEMA_VERSION = 1;

export const TABS = [
  { id: 'technical', label: 'Price & Deviation' },
  { id: 'valuation', label: 'Valuation & Ratings' },
  { id: 'quality', label: 'Fundamentals & Risk' },
  { id: 'events', label: 'Trends & Events' },
];

export const UNAVAILABLE_SPEC_FILTERS = [
  'Top Rank (rankFilter)',
  'FCF Yield (fcfYieldFilter)',
  'Target Upside (targetUpsideFilter)',
  'Altman Z (altmanFilter)',
  'ROIC (roicFilter)',
  'Debt/Equity (deRatioFilter)',
  'Earnings date (earningsFilter)',
  'IA9 configured watchlist',
];

// ---------------------------------------------------------------------------
// Data adapter — normalize raw API records at the ingestion boundary.
// ---------------------------------------------------------------------------

const NUMERIC_TEXT = /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i;

/** Finite number or null. Never coerces null/''/booleans/'N/A'/NaN to zero. */
export function toNumber(value) {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const text = value.trim();
    if (!NUMERIC_TEXT.test(text)) return null;
    const n = Number(text);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Remove binary floating-point noise (0.3 * 100 = 30.000000000000004). */
function cleanFloat(n) {
  return Number(n.toPrecision(12));
}

/** Pipeline stores percentages as fractions (0.20); spec uses points (20). */
function toPercentPoints(value) {
  const n = toNumber(value);
  return n === null ? null : cleanFloat(n * 100);
}

function toInteger(value) {
  const n = toNumber(value);
  return n !== null && Number.isInteger(n) ? n : null;
}

function toCategory(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!text || /^n\/?a$/i.test(text)) return null;
  return text;
}

function toSlopeFlag(value) {
  const n = toNumber(value);
  if (n === null) return null;
  return n > 0;
}

const RATING_GROUPS = {
  'strong buy': 'buy',
  buy: 'buy',
  a: 'buy',
  b: 'buy',
  s: 'buy',
  neutral: 'hold',
  hold: 'hold',
  c: 'hold',
  sell: 'sell',
  'strong sell': 'sell',
  d: 'sell',
  f: 'sell',
};

export function toRatingGroup(value) {
  if (value === null || value === undefined) return null;
  const key = String(value).trim().toLowerCase().replace(/[+-]$/, '');
  return RATING_GROUPS[key] || null;
}

const RATING_RANKS = { 'strong buy': 5, buy: 4, neutral: 3, hold: 3, sell: 2, 'strong sell': 1 };

/** Ordinal for sorting ratings (Strong Buy = 5 … Strong Sell = 1). */
function toRatingRank(value) {
  if (value === null || value === undefined) return null;
  return RATING_RANKS[String(value).trim().toLowerCase()] ?? null;
}

function toCrossClassification(value) {
  const text = String(value || '').toLowerCase();
  if (text.includes('above')) return 'golden';
  if (text.includes('below')) return 'death';
  return null;
}

export function normalizeStock(raw = {}) {
  const ticker = String(raw.symbol || '').trim().toUpperCase();
  const fairValue = toNumber(raw.dcf);
  const priceToFairValue = toNumber(raw.price_to_dcf_ratio);
  const peg = toNumber(raw.price_earnings_to_growth_ratio_ttm ?? raw.priceEarningsToGrowthRatioTTM);
  const ps = toNumber(raw.price_sales_ratio_ttm ?? raw.priceSalesRatioTTM);
  const piotroski = toInteger(raw.piotroski_score ?? raw.piotroskiScore);
  const debtRatio = toNumber(raw.debt_ratio_ttm ?? raw.debtRatioTTM);
  const peRatio = toNumber(raw.price_earnings_ratio_ttm ?? raw.priceEarningsRatioTTM);
  const pbRatio = toNumber(raw.price_to_book_ratio_ttm ?? raw.priceToBookRatioTTM);
  const currentRatio = toNumber(raw.current_ratio_ttm ?? raw.currentRatioTTM);
  const divYieldRaw = toNumber(raw.dividend_yield_ttm ?? raw.dividendYielTTM);
  // Stub records (FMP had no data) are zero-filled by the pipeline, and debt
  // ratio is meaningless for ETFs: both are "no data", never "no debt".
  const isStub = String(raw.financial_data_source || '').trim().toLowerCase() === 'stub';
  const isEtf = raw.is_etf === true || raw.is_etf === 1 || String(raw.is_etf).toLowerCase() === 'true';

  return {
    id: raw.id,
    ticker,
    companyName: toCategory(raw.company_name ?? raw.companyName),
    sector: toCategory(raw.sector),
    industry: toCategory(raw.industry),
    zscore_21: toNumber(raw.z_score_21d),
    zscore_60: toNumber(raw.z_score_60d),
    zscore_200: toNumber(raw.z_score_200d),
    sma_200_slope_positive: toSlopeFlag(raw.sma200_slope),
    pct_from_sma_200: toPercentPoints(raw.pcnt_from_sma200),
    pcnt_upside_in_range_6m: toPercentPoints(raw.pcnt_upside_in_range_6m),
    peg_ratio: peg,
    ps_ratio: ps !== null && ps >= 0 ? ps : null,
    // Stub/ETF rows are zero-filled by the pipeline, and a negative P/E means
    // trailing losses: both are "no P/E", never a cheap multiple.
    pe_ratio: !isStub && !isEtf && peRatio !== null && peRatio > 0 ? peRatio : null,
    analyst_rating: toRatingGroup(raw.rating_recommendation ?? raw.ratingRecommendation),
    analyst_rating_label: toCategory(raw.rating_recommendation ?? raw.ratingRecommendation),
    analyst_rating_rank: toRatingRank(raw.rating_recommendation ?? raw.ratingRecommendation),
    fair_value: fairValue !== null && fairValue > 0 ? fairValue : null,
    price_to_fair_value: priceToFairValue !== null && priceToFairValue > 0 ? priceToFairValue : null,
    piotroski_f_score: piotroski !== null && piotroski >= 0 && piotroski <= 9 ? piotroski : null,
    debt_ratio: !isStub && !isEtf && debtRatio !== null && debtRatio >= 0 ? debtRatio : null,
    // Value inputs. P/B and current ratio are meaningless when negative or
    // stub-zero-filled; dividend yield is a fraction (FMP) kept in percent
    // points, and a genuine 0 simply means "pays no dividend".
    pb_ratio: !isStub && !isEtf && pbRatio !== null && pbRatio > 0 ? pbRatio : null,
    current_ratio: !isStub && !isEtf && currentRatio !== null && currentRatio > 0 ? currentRatio : null,
    div_yield: !isStub && !isEtf && divYieldRaw !== null && divYieldRaw >= 0 ? divYieldRaw * 100 : null,
    roe: toPercentPoints(raw.return_on_equity_ttm ?? raw.returnOnEquityTTM),
    rule_of_40: toPercentPoints(raw.rule_of_40),
    rev_gr_1yr: toPercentPoints(raw.revenue_1yr_growth),
    rev_gr_3yr: toPercentPoints(raw.revenue_3yr_growth),
    forecast_rev_gr: toPercentPoints(raw.forecast_next_year_revenue_growth_rate),
    trend_state: [0, 1].includes(toInteger(raw.trend_state)) ? toInteger(raw.trend_state) : null,
    signal_score: toNumber(raw.signal_score),
    predicted_signal_score: toNumber(raw.predicted_signal_score),
    n_days_since_cross_over: toInteger(raw.n_days_since_cross_over),
    golden_cross: toCrossClassification(raw.sma_50_200),
    rsi: toNumber(raw.rsi),
    has_chart: Boolean(String(raw.short_fname || '').trim()),
    raw,
  };
}

// ---------------------------------------------------------------------------
// Predicate helpers. Every helper fails when its own input is missing.
// ---------------------------------------------------------------------------

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const above = (field, n) => (s) => isNum(s[field]) && s[field] > n;
const below = (field, n) => (s) => isNum(s[field]) && s[field] < n;
/** Inclusive range: both endpoints match. */
const betweenInclusive = (field, lo, hi) => (s) => isNum(s[field]) && s[field] >= lo && s[field] <= hi;

const zScoreOptions = (field) => [
  { value: 'lt-1', label: '< -1', test: below(field, -1) },
  { value: 'lt-2', label: '< -2', test: below(field, -2) },
  { value: 'lt-3', label: '< -3', test: below(field, -3) },
  { value: 'gt1', label: '> 1', test: above(field, 1) },
  { value: 'gt2', label: '> 2', test: above(field, 2) },
  { value: 'gt3', label: '> 3', test: above(field, 3) },
];

const gtPercentOptions = (field, thresholds) => thresholds.map((n) => ({
  value: `gt${n}`,
  label: `> ${n}%`,
  test: above(field, n),
}));

const slopeIs = (flag) => (s) => s.sma_200_slope_positive === flag;
const priceBelowSma = (s) => isNum(s.pct_from_sma_200) && s.pct_from_sma_200 < 0;
const priceAtOrAboveSma = (s) => isNum(s.pct_from_sma_200) && s.pct_from_sma_200 >= 0;
const positivePeg = (s) => isNum(s.peg_ratio) && s.peg_ratio > 0;
const validPiotroski = (s) => Number.isInteger(s.piotroski_f_score);
const dx = (s) => s.n_days_since_cross_over;
const hasDx = (s) => Number.isInteger(dx(s));

const crossoverOptions = [].concat(
  [2, 5, 10, 20].map((n) => ({
    value: `newup${n}`,
    label: `New up < ${n} days`,
    test: (s) => hasDx(s) && dx(s) >= 0 && dx(s) < n,
  })),
  [2, 5, 10, 20].map((n) => ({
    value: `newdown-${n}`,
    label: `New down < ${n} days`,
    test: (s) => hasDx(s) && dx(s) < 0 && dx(s) > -n,
  })),
);

// ---------------------------------------------------------------------------
// Filter registry.
// ---------------------------------------------------------------------------

export const FILTERS = [
  // Always-visible quick filters
  {
    id: 'tickerFilter',
    category: 'quick',
    label: 'Tickers',
    type: 'text',
    help: 'Comma- or space-separated symbols. Exact symbol match, not substring search.',
  },
  {
    id: 'sectorFilter',
    category: 'quick',
    label: 'Sector',
    type: 'dynamic',
    field: 'sector',
    help: 'Exact sector match.',
  },
  {
    id: 'industryFilter',
    category: 'quick',
    label: 'Industry',
    type: 'dynamic',
    field: 'industry',
    help: 'Exact industry match. When a Sector is selected, only that sector\'s industries are listed.',
  },

  // Price & Deviation
  {
    id: 'zscore21Filter',
    category: 'technical',
    label: 'Z 21d',
    help: 'Price deviation from its 21-day mean, in standard deviations.',
    options: zScoreOptions('zscore_21'),
  },
  {
    id: 'zscore60Filter',
    category: 'technical',
    label: 'Z 60d',
    help: 'Price deviation from its 60-day mean, in standard deviations. (The pipeline computes a 60-day lookback rather than 50-day.)',
    options: zScoreOptions('zscore_60'),
  },
  {
    id: 'zscore200Filter',
    category: 'technical',
    label: 'Z 200d',
    help: 'Price deviation from its 200-day mean, in standard deviations.',
    options: zScoreOptions('zscore_200'),
  },
  {
    id: 'sma200Filter',
    category: 'technical',
    label: '200d SMA',
    help: 'Price position relative to the 200-day simple moving average and the direction of its slope. Price equal to the SMA counts as At/above.',
    options: [
      { value: 'positive_slope', label: 'Positive slope', test: slopeIs(true) },
      { value: 'negative_slope', label: 'Negative slope', test: slopeIs(false) },
      { value: 'below_positive', label: 'Below, positive slope', test: (s) => slopeIs(true)(s) && priceBelowSma(s) },
      { value: 'above_positive', label: 'At/above, positive slope', test: (s) => slopeIs(true)(s) && priceAtOrAboveSma(s) },
      { value: 'below_negative', label: 'Below, negative slope', test: (s) => slopeIs(false)(s) && priceBelowSma(s) },
      { value: 'above_negative', label: 'At/above, negative slope', test: (s) => slopeIs(false)(s) && priceAtOrAboveSma(s) },
    ],
  },
  {
    id: 'upside6mFilter',
    category: 'technical',
    label: 'Upside 6m',
    help: 'Upside to the top of the six-month price range. A historical range metric, not a return prediction.',
    options: gtPercentOptions('pcnt_upside_in_range_6m', [20, 25, 40, 60, 80]),
  },

  // Valuation & Ratings
  {
    id: 'pegFilter',
    category: 'valuation',
    label: 'PEG',
    help: 'Price/earnings-to-growth ratio. Zero or negative PEG never matches.',
    options: [
      { value: 'lt1', label: '< 1', test: (s) => positivePeg(s) && s.peg_ratio < 1 },
      { value: 'lt2', label: '< 2', test: (s) => positivePeg(s) && s.peg_ratio < 2 },
      { value: 'gt2', label: '> 2', test: (s) => positivePeg(s) && s.peg_ratio > 2 },
    ],
  },
  {
    id: 'psRatioFilter',
    category: 'valuation',
    label: 'P/S',
    help: 'Price-to-sales ratio (TTM), strictly below the selected limit.',
    options: [2, 3, 5, 10, 20, 50].map((n) => ({ value: `lt${n}`, label: `< ${n}`, test: below('ps_ratio', n) })),
  },
  {
    id: 'peRatioFilter',
    category: 'valuation',
    label: 'P/E',
    help: 'Price-to-earnings ratio (TTM). "Between" ranges are inclusive of both endpoints. Loss-making (negative) P/E and records without FMP data (ETFs, stubs) never match.',
    options: [
      { value: 'lt15', label: '< 15', test: below('pe_ratio', 15) },
      { value: 'gt0', label: '> 0', test: above('pe_ratio', 0) },
      ...[10, 20, 30, 40, 50, 75, 100].map((n) => ({
        value: `1to${n}`,
        label: `Between 1 and ${n}`,
        test: betweenInclusive('pe_ratio', 1, n),
      })),
      { value: 'gt100', label: '> 100', test: above('pe_ratio', 100) },
    ],
  },
  {
    id: 'analystRatingFilter',
    category: 'valuation',
    label: 'FMP Rating',
    help: 'FMP composite financial rating (not analyst consensus). Buy = Strong Buy/Buy, Hold = Neutral, Sell = Sell/Strong Sell. Unrated stocks never match.',
    options: [
      { value: 'buy', label: 'Buy', test: (s) => s.analyst_rating === 'buy' },
      { value: 'hold', label: 'Hold', test: (s) => s.analyst_rating === 'hold' },
      { value: 'sell', label: 'Sell', test: (s) => s.analyst_rating === 'sell' },
      { value: 'hold_or_buy', label: 'Hold or Buy', test: (s) => s.analyst_rating === 'hold' || s.analyst_rating === 'buy' },
      { value: 'hold_or_sell', label: 'Hold or Sell', test: (s) => s.analyst_rating === 'hold' || s.analyst_rating === 'sell' },
    ],
  },

  {
    id: 'pbRatioFilter',
    category: 'valuation',
    label: 'P/B',
    help: 'Price-to-book ratio (TTM), strictly below the selected limit. Negative book value and records without FMP data (ETFs, stubs) never match.',
    options: [1, 1.5, 2, 3].map((n) => ({ value: `lt${n}`, label: `< ${n}`, test: below('pb_ratio', n) })),
  },
  {
    id: 'divYieldFilter',
    category: 'valuation',
    label: 'Div Yield',
    help: 'Trailing twelve-month dividend yield, in percent. "Any" requires a positive yield (pays a dividend); stub/ETF records never match.',
    options: [
      { value: 'gt0', label: 'Any (> 0%)', test: above('div_yield', 0) },
      { value: 'gt2', label: '> 2%', test: above('div_yield', 2) },
      { value: 'gt4', label: '> 4%', test: above('div_yield', 4) },
    ],
  },
  {
    id: 'currentRatioFilter',
    category: 'quality',
    label: 'Current Ratio',
    help: 'Current assets ÷ current liabilities (TTM). Graham-style liquidity floor; inclusive of the selected value. Records without FMP data never match.',
    options: [
      { value: 'ge1.5', label: '≥ 1.5', test: (s) => isNum(s.current_ratio) && s.current_ratio >= 1.5 },
      { value: 'ge2', label: '≥ 2 (Graham)', test: (s) => isNum(s.current_ratio) && s.current_ratio >= 2 },
    ],
  },

  // Fundamentals & Risk
  {
    id: 'piotroskiFilter',
    category: 'quality',
    label: 'Piotroski',
    help: 'Piotroski F-Score (0–9) measuring financial strength.',
    options: [
      { value: 'ge7', label: '7–9 (Strong)', test: (s) => validPiotroski(s) && s.piotroski_f_score >= 7 },
      { value: 'ge5', label: '5–9', test: (s) => validPiotroski(s) && s.piotroski_f_score >= 5 },
      { value: 'ge4', label: '4–9', test: (s) => validPiotroski(s) && s.piotroski_f_score >= 4 },
      { value: 'le3', label: '0–3 (Weak)', test: (s) => validPiotroski(s) && s.piotroski_f_score <= 3 },
    ],
  },
  {
    id: 'debtRatioFilter',
    category: 'quality',
    label: 'Debt Ratio',
    help: 'Total debt ÷ total assets (FMP, TTM). Lower = less leverage risk. ETFs and stocks without FMP financial data never match.',
    options: [
      { value: 'lt0.1', label: 'Very low (< 0.1)', test: below('debt_ratio', 0.1) },
      { value: 'lt0.3', label: 'Low (< 0.3)', test: below('debt_ratio', 0.3) },
      { value: 'lt0.5', label: 'Moderate or lower (< 0.5)', test: below('debt_ratio', 0.5) },
      { value: 'gt0.5', label: 'High (> 0.5)', test: above('debt_ratio', 0.5) },
      { value: 'gt0.7', label: 'Very high (> 0.7)', test: above('debt_ratio', 0.7) },
    ],
  },
  {
    id: 'roeFilter',
    category: 'quality',
    label: 'ROE',
    help: 'Return on equity (TTM).',
    options: gtPercentOptions('roe', [10, 20, 30]),
  },
  {
    id: 'ruleOf40Filter',
    category: 'quality',
    label: 'Rule of 40',
    help: 'Revenue growth plus profit margin, in percentage points. Not a price multiple.',
    options: [0, 20, 40, 60, 80].map((n) => ({ value: `gt${n}`, label: `> ${n}`, test: above('rule_of_40', n) })),
  },
  {
    id: 'revGr1Filter',
    category: 'quality',
    label: 'Revenue Growth 1y',
    help: 'Historical one-year revenue growth.',
    options: gtPercentOptions('rev_gr_1yr', [5, 10, 20, 30]),
  },
  {
    id: 'revGr3Filter',
    category: 'quality',
    label: 'Revenue CAGR 3y',
    help: 'Historical three-year compound annual revenue growth.',
    options: gtPercentOptions('rev_gr_3yr', [5, 10, 20, 30]),
  },
  {
    id: 'revGrForecastFilter',
    category: 'quality',
    label: 'Forecast Revenue Growth',
    help: 'Forecast next-year revenue growth.',
    options: gtPercentOptions('forecast_rev_gr', [5, 10, 20, 30, 40, 50]),
  },

  // Trends & Events
  {
    id: 'trendStateFilter',
    category: 'events',
    label: 'Trend',
    help: 'Turning down = uptrend, signal score ≤ 2, predicted score 0. Turning up = downtrend, signal score ≥ 4, predicted score 5. Recency uses trading days since the signal crossover.',
    options: [
      { value: '0', label: 'Down', test: (s) => s.trend_state === 0 },
      { value: '1', label: 'Up', test: (s) => s.trend_state === 1 },
      {
        value: '1turningdown',
        label: 'Up, turning down',
        test: (s) => s.trend_state === 1 && isNum(s.signal_score) && s.signal_score <= 2
          && s.predicted_signal_score === 0,
      },
      {
        value: '0turningup',
        label: 'Down, turning up',
        test: (s) => s.trend_state === 0 && isNum(s.signal_score) && s.signal_score >= 4
          && s.predicted_signal_score === 5,
      },
      { value: '1in10days', label: 'Up, within 10 days', test: (s) => s.trend_state === 1 && hasDx(s) && dx(s) >= 0 && dx(s) < 10 },
      { value: '0in10days', label: 'Down, within 10 days', test: (s) => s.trend_state === 0 && hasDx(s) && dx(s) < 0 && dx(s) > -10 },
    ],
  },
  {
    id: 'daysXoverFilter',
    category: 'events',
    label: 'X-Over',
    help: 'Trading days since the signal crossover. Day 0 counts as an upward crossover; the exact cutoff day is excluded.',
    options: crossoverOptions,
  },
  {
    id: 'goldenCrossFilter',
    category: 'events',
    label: '50/200 Cross',
    help: 'Golden Cross = 50-day SMA above 200-day SMA; Death Cross = below. No recency is implied.',
    options: [
      { value: 'golden', label: 'Golden Cross', test: (s) => s.golden_cross === 'golden' },
      { value: 'death', label: 'Death Cross', test: (s) => s.golden_cross === 'death' },
    ],
  },
];

export const FILTERS_BY_ID = Object.fromEntries(FILTERS.map((f) => [f.id, f]));
export const FILTER_IDS = FILTERS.map((f) => f.id);

// ---------------------------------------------------------------------------
// State helpers.
// ---------------------------------------------------------------------------

export function defaultValue(id) {
  return id === 'tickerFilter' ? '' : 'all';
}

export function createDefaultState() {
  return Object.fromEntries(FILTER_IDS.map((id) => [id, defaultValue(id)]));
}

/** Split on commas/whitespace, trim, uppercase, drop empties, dedupe. */
export function parseTickers(text) {
  const tokens = String(text || '').split(/[\s,]+/).map((t) => t.trim().toUpperCase()).filter(Boolean);
  return [...new Set(tokens)];
}

export function isFilterActive(id, value) {
  if (id === 'tickerFilter') return parseTickers(value).length > 0;
  return value !== undefined && value !== null && value !== '' && value !== 'all';
}

function optionFor(id, value) {
  const filter = FILTERS_BY_ID[id];
  return filter && filter.options ? filter.options.find((o) => o.value === value) : undefined;
}

/**
 * Compile a filter state into a list of active predicate functions.
 * Throws on an unknown active option — never silently treat it as a match.
 */
export function compilePredicates(state = {}) {
  const predicates = [];
  FILTER_IDS.forEach((id) => {
    const value = state[id];
    if (!isFilterActive(id, value)) return;
    const filter = FILTERS_BY_ID[id];
    if (id === 'tickerFilter') {
      const tickers = new Set(parseTickers(value));
      predicates.push((s) => tickers.has(s.ticker));
    } else if (filter.type === 'dynamic') {
      const target = String(value).trim();
      predicates.push((s) => s[filter.field] === target);
    } else {
      const option = optionFor(id, value);
      if (!option) throw new Error(`Unknown option "${value}" for filter ${id}`);
      predicates.push(option.test);
    }
  });
  return predicates;
}

/** Canonical predicate: AND across every active control. */
export function matchesStock(stock, state) {
  return compilePredicates(state).every((test) => test(stock));
}

/** Filter a normalized universe once, compiling predicates a single time. */
export function filterStocks(stocks, state) {
  const predicates = compilePredicates(state);
  if (predicates.length === 0) return stocks.slice();
  return stocks.filter((s) => predicates.every((test) => test(s)));
}

export function countActiveByTab(state = {}) {
  const counts = Object.fromEntries(TABS.map((t) => [t.id, 0]));
  FILTERS.forEach((f) => {
    if (counts[f.category] !== undefined && isFilterActive(f.id, state[f.id])) counts[f.category] += 1;
  });
  return counts;
}

/** Human-readable active criteria, including quick filters. */
export function describeCriteria(state = {}) {
  return FILTERS.filter((f) => isFilterActive(f.id, state[f.id])).map((f) => {
    let valueLabel = state[f.id];
    if (f.id === 'tickerFilter') valueLabel = parseTickers(state[f.id]).join(', ');
    else if (f.options) valueLabel = optionFor(f.id, state[f.id])?.label ?? String(state[f.id]);
    return { id: f.id, label: f.label, valueLabel };
  });
}

/**
 * Validate a persisted state. Defaults the whole state, then overlays valid
 * saved values. Returns errors for unknown IDs or invalid options so the
 * caller can reject the load with an actionable message.
 */
/** Filters that existed in earlier versions; saved screens using them still load. */
export const RETIRED_FILTER_IDS = ['fairValueFilter'];

export function validateSavedState(saved) {
  const state = createDefaultState();
  const errors = [];
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) {
    return { state, errors: ['Saved filter state is not an object.'] };
  }
  Object.entries(saved).forEach(([id, value]) => {
    const filter = FILTERS_BY_ID[id];
    if (!filter) {
      if (RETIRED_FILTER_IDS.includes(id)) return;
      if (isFilterActive(id, value)) errors.push(`"${id}" is not an available filter.`);
      return;
    }
    if (typeof value !== 'string') {
      errors.push(`${filter.label} has an invalid value.`);
      return;
    }
    if (filter.options && value !== 'all' && !optionFor(id, value)) {
      errors.push(`${filter.label} has an unknown option "${value}".`);
      return;
    }
    state[id] = value;
  });
  return { state, errors };
}

// ---------------------------------------------------------------------------
// Additive group presets.
// ---------------------------------------------------------------------------

export const PRESET_GROUPS = {
  deviation: ['zscore21Filter', 'zscore60Filter', 'zscore200Filter'],
  quality: ['piotroskiFilter', 'debtRatioFilter'],
  growth: ['revGr1Filter', 'revGr3Filter', 'revGrForecastFilter', 'ruleOf40Filter', 'roeFilter'],
  watchlist: ['tickerFilter'],
  upside: ['upside6mFilter'],
  value: ['peRatioFilter', 'pegFilter', 'psRatioFilter', 'pbRatioFilter', 'currentRatioFilter', 'divYieldFilter'],
};

export const PRESETS = [
  { id: 'minorDip', label: 'Minor Dip', group: 'deviation', values: { zscore21Filter: 'lt-2' }, help: 'Z 21d < -2' },
  { id: 'mediumDip', label: 'Medium Dip', group: 'deviation', values: { zscore60Filter: 'lt-2' }, help: 'Z 60d < -2' },
  { id: 'majorDip', label: 'Major Dip', group: 'deviation', values: { zscore200Filter: 'lt-2' }, help: 'Z 200d < -2' },
  {
    id: 'upside25',
    label: 'Upside > 25%',
    group: 'upside',
    values: { upside6mFilter: 'gt25' },
    help: 'Upside to the 6-month high > 25%',
  },
  {
    id: 'value',
    label: 'Value',
    group: 'value',
    values: {
      peRatioFilter: 'lt15',
      pegFilter: 'lt1',
      psRatioFilter: 'lt2',
      pbRatioFilter: 'lt1.5',
      currentRatioFilter: 'ge1.5',
      divYieldFilter: 'gt0',
      debtRatioFilter: 'lt0.5',
    },
    help: 'Graham-style value screen: P/E < 15, PEG < 1, P/S < 2, P/B < 1.5, Current Ratio ≥ 1.5, Debt Ratio < 0.5, and pays a dividend.',
  },
  {
    id: 'quality',
    label: 'Quality',
    group: 'quality',
    values: { piotroskiFilter: 'ge4', debtRatioFilter: 'lt0.3' },
    help: 'Piotroski 4–9 and Debt Ratio < 0.3 (low leverage). Altman Z is not available in this dataset.',
  },
  {
    id: 'growth',
    label: 'Growth',
    group: 'growth',
    values: { revGr1Filter: 'gt5', revGrForecastFilter: 'gt10', ruleOf40Filter: 'gt20' },
    help: 'Revenue 1y > 5%, Forecast revenue > 10%, Rule of 40 > 20',
  },
  {
    id: 'highGrowth',
    label: 'High Growth',
    group: 'growth',
    values: {
      revGr1Filter: 'gt20',
      revGr3Filter: 'gt10',
      revGrForecastFilter: 'gt20',
      ruleOf40Filter: 'gt40',
      roeFilter: 'gt10',
    },
    help: 'Revenue 1y > 20%, 3y CAGR > 10%, Forecast > 20%, Rule of 40 > 40, ROE > 10% (ROIC not available)',
  },
];

function presetTargetValue(preset, id) {
  return preset.values[id] ?? defaultValue(id);
}

function sameValue(id, a, b) {
  if (id === 'tickerFilter') {
    const left = parseTickers(a);
    const right = parseTickers(b);
    return left.length === right.length && left.every((t) => right.includes(t));
  }
  return (a ?? defaultValue(id)) === (b ?? defaultValue(id));
}

/** A preset is highlighted only when its group exactly matches its values. */
export function isPresetActive(state, preset) {
  if (preset.group === 'watchlist' && parseTickers(preset.values.tickerFilter).length === 0) return false;
  return PRESET_GROUPS[preset.group].every((id) => sameValue(id, state[id], presetTargetValue(preset, id)));
}

/**
 * Apply a preset additively: only its group's controls change. Clicking the
 * active preset clears that group back to defaults.
 */
export function applyPreset(state, preset) {
  const next = { ...state };
  const owned = PRESET_GROUPS[preset.group];
  const wasActive = isPresetActive(state, preset);
  owned.forEach((id) => { next[id] = defaultValue(id); });
  if (!wasActive) Object.assign(next, preset.values);
  return next;
}

export function watchlistPreset(tickers) {
  return {
    id: 'myWatch',
    label: 'MyWatch',
    group: 'watchlist',
    values: { tickerFilter: parseTickers(tickers).join(', ') },
    help: 'Your editable watchlist',
  };
}

// ---------------------------------------------------------------------------
// Derived outputs.
// ---------------------------------------------------------------------------

export function buildDistinctOptions(stocks, field) {
  return [...new Set(stocks.map((s) => s[field]).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/** Industries available for a sector ('all' or empty = every industry), from the data's own mapping. */
export function industriesForSector(stocks, sector) {
  if (!sector || sector === 'all') return buildDistinctOptions(stocks, 'industry');
  return buildDistinctOptions(stocks.filter((s) => s.sector === sector), 'industry');
}

/**
 * Apply a filter change. Selecting a sector clears an industry that is not
 * part of it, so the Industry dropdown never holds a hidden, unreachable value.
 */
export function updateFilterState(state, id, value, stocks = []) {
  const next = { ...state, [id]: value };
  if (id === 'sectorFilter' && isFilterActive('industryFilter', next.industryFilter)) {
    if (!industriesForSector(stocks, value).includes(next.industryFilter)) next.industryFilter = defaultValue('industryFilter');
  }
  return next;
}

export const SORT_OPTIONS = [
  { value: 'ticker:1', label: 'Ticker (A → Z)', field: 'ticker', dir: 1 },
  { value: 'zscore_21:1', label: 'Z 21d (low → high)', field: 'zscore_21', dir: 1 },
  { value: 'pcnt_upside_in_range_6m:-1', label: 'Upside 6m (high → low)', field: 'pcnt_upside_in_range_6m', dir: -1 },
  { value: 'rule_of_40:-1', label: 'Rule of 40 (high → low)', field: 'rule_of_40', dir: -1 },
  { value: 'forecast_rev_gr:-1', label: 'Forecast Revenue Growth (high → low)', field: 'forecast_rev_gr', dir: -1 },
  { value: 'n_days_since_cross_over:1', label: 'Days since crossover (low → high)', field: 'n_days_since_cross_over', dir: 1 },
];

export const DEFAULT_SORT = { field: SORT_OPTIONS[0].field, dir: SORT_OPTIONS[0].dir };

export const sortKey = (sort) => `${sort.field}:${sort.dir}`;

/** Accepts { field, dir } or a SORT_OPTIONS value such as 'rule_of_40:-1'. */
export function resolveSort(sort) {
  if (sort && typeof sort === 'object' && sort.field) return { field: sort.field, dir: sort.dir === 1 ? 1 : -1 };
  const option = SORT_OPTIONS.find((o) => o.value === sort);
  return option ? { field: option.field, dir: option.dir } : DEFAULT_SORT;
}

/**
 * Column-header click: the same column flips direction; a new column starts
 * at its natural direction (text A→Z, numbers high→low).
 */
export function nextSort(current, field, defaultDir = -1) {
  if (current && current.field === field) return { field, dir: current.dir === 1 ? -1 : 1 };
  return { field, dir: defaultDir };
}

/** Stable sort; missing values always sort last. Never changes inclusion. */
export function sortStocks(stocks, sort) {
  const { field, dir } = resolveSort(sort);
  return stocks
    .map((s, index) => ({ s, index }))
    .sort((a, b) => {
      const av = a.s[field];
      const bv = b.s[field];
      const aMissing = av === null || av === undefined || av === '';
      const bMissing = bv === null || bv === undefined || bv === '';
      if (aMissing || bMissing) return aMissing === bMissing ? a.index - b.index : aMissing ? 1 : -1;
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));
      return cmp === 0 ? a.index - b.index : cmp * dir;
    })
    .map(({ s }) => s);
}

export const CSV_COLUMNS = [
  ['Ticker', 'ticker'],
  ['Company', 'companyName'],
  ['Sector', 'sector'],
  ['Industry', 'industry'],
  ['Trend State', 'trend_state'],
  ['Signal Score', 'signal_score'],
  ['Predicted Signal Score', 'predicted_signal_score'],
  ['Days Since Crossover', 'n_days_since_cross_over'],
  ['Z 21d', 'zscore_21'],
  ['Z 60d', 'zscore_60'],
  ['Z 200d', 'zscore_200'],
  ['200d SMA Slope Positive', 'sma_200_slope_positive'],
  ['% From SMA200', 'pct_from_sma_200'],
  ['Upside 6m %', 'pcnt_upside_in_range_6m'],
  ['PEG', 'peg_ratio'],
  ['P/E', 'pe_ratio'],
  ['P/S', 'ps_ratio'],
  ['P/B', 'pb_ratio'],
  ['Current Ratio', 'current_ratio'],
  ['Div Yield %', 'div_yield'],
  ['FMP Rating', 'analyst_rating_label'],
  ['DCF Fair Value', 'fair_value'],
  ['Price / DCF', 'price_to_fair_value'],
  ['Piotroski', 'piotroski_f_score'],
  ['Debt Ratio', 'debt_ratio'],
  ['ROE %', 'roe'],
  ['Rule of 40', 'rule_of_40'],
  ['Revenue Growth 1y %', 'rev_gr_1yr'],
  ['Revenue CAGR 3y %', 'rev_gr_3yr'],
  ['Forecast Revenue Growth %', 'forecast_rev_gr'],
  ['50/200 Cross', 'golden_cross'],
  ['RSI', 'rsi'],
];

function csvCell(value) {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildCsv(stocks) {
  const header = CSV_COLUMNS.map(([label]) => csvCell(label)).join(',');
  const rows = stocks.map((s) => CSV_COLUMNS.map(([, key]) => csvCell(s[key])).join(','));
  return [header, ...rows].join('\n');
}

/**
 * Clickable trend tiles. Each maps to a Trend filter option, so clicking a
 * tile is exactly the same as picking that option in the Trend dropdown.
 */
export const TREND_TILES = [
  { value: '1', label: 'Uptrend %', help: 'Trend state is Up' },
  { value: '0', label: 'Downtrend %', help: 'Trend state is Down' },
  { value: '1in10days', label: 'New Uptrend %', help: 'Trend state is Up and the signal crossover was within the last 10 trading days' },
  { value: '0in10days', label: 'New Downtrend %', help: 'Trend state is Down and the signal crossover was within the last 10 trading days' },
];

/**
 * Trend tile figures over `stocks`: count and % of stocks with a known trend
 * state. Callers pass the set matched by every filter EXCEPT Trend, so the
 * tiles keep showing the breakdown after one of them is clicked.
 */
export function summarizeTrends(stocks) {
  const known = stocks.filter((s) => s.trend_state === 0 || s.trend_state === 1).length;
  return Object.fromEntries(TREND_TILES.map((tile) => {
    const option = optionFor('trendStateFilter', tile.value);
    const count = stocks.filter(option.test).length;
    return [tile.value, { count, pct: known ? (count / known) * 100 : null }];
  }));
}

export function summarizeMatches(stocks) {
  const total = stocks.length;
  const withTrend = stocks.filter((s) => s.trend_state !== null);
  const ss = stocks.filter((s) => isNum(s.signal_score)).map((s) => s.signal_score);
  const pss = stocks.filter((s) => isNum(s.predicted_signal_score)).map((s) => s.predicted_signal_score);
  const both = stocks.filter((s) => isNum(s.signal_score) && isNum(s.predicted_signal_score));
  const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
  const pct = (n, d) => (d ? (n / d) * 100 : null);
  const median = (arr) => {
    if (!arr.length) return null;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  const ratio = (n, d) => (n !== null && d ? n / d : null);
  const meanSignal = mean(ss);
  const meanPredicted = mean(pss);
  const medianSignal = median(ss);
  const medianPredicted = median(pss);
  return {
    total,
    uptrendPct: pct(withTrend.filter((s) => s.trend_state === 1).length, withTrend.length),
    downtrendPct: pct(withTrend.filter((s) => s.trend_state === 0).length, withTrend.length),
    meanSignal,
    meanPredicted,
    // Mean PSS ÷ Mean SS. Null when mean SS is 0 (e.g. every match has SS 0).
    pssToSsRatio: ratio(meanPredicted, meanSignal),
    medianSignal,
    medianPredicted,
    // Median PSS ÷ Median SS. Null when median SS is 0.
    medianPssToSsRatio: ratio(medianPredicted, medianSignal),
    improvingPct: pct(both.filter((s) => s.predicted_signal_score > s.signal_score).length, both.length),
    weakeningPct: pct(both.filter((s) => s.predicted_signal_score < s.signal_score).length, both.length),
  };
}
